import { FC, useEffect, useRef } from 'react';
import { Flex, Typography } from 'antd';
import dayjs from 'dayjs';
import {
  SDropdown,
  ErrorMessage,
  SLabelTitle,
  SMailSelector,
  ModalDialog,
  StatusDropdown,
  UploadButton,
  SInput,
  STextArea,
  SDatePicker,
} from '../..';
import { IProps } from './type';
import styles from './index.module.scss';
import globalStyles from '../../../assets/styles/index.module.scss';
import { useAppSelector } from '../../../store';
import { DATE_FORMAT } from '../../../assets/conts';
import { modalTypes } from '../../../assets/enums';
import { useSetOrganizationOnOpen } from '../../../hooks';

const DamageNotificationModal: FC<IProps> = (props) => {
  const { getFieldProps, setFieldValue, values, errors, touched } =
    props.formik;
  const uploadInput = useRef<HTMLInputElement | null>(null);
  const { organization } = useAppSelector(
    (state) => state.userReducer.userInfo,
  );

  useEffect(() => {
    if (values.damage_type === 'domestic') setFieldValue('country', 'TR');
    else if (values.damage_type === 'abroad') setFieldValue('city', '');
    else if (values.damage_type === 'transportation') {
      setFieldValue('city', '');
      setFieldValue('country', '');
      setFieldValue('location_code', '');
    }
  }, [values.damage_type, setFieldValue]);

  useSetOrganizationOnOpen(props.open, values, setFieldValue);

  return (
    <ModalDialog
      title={props.title}
      subTitle={props.subTitle}
      open={props.open}
      handleOk={props.handleSubmit}
      handleCancel={props.handleCancel}
    >
      <Flex gap="middle" vertical>
        <Flex align="flex-start" justify="space-between">
          <SLabelTitle>Şirket Bilgileri</SLabelTitle>
          <Flex vertical gap="small">
            <SDropdown
              id="organization"
              value={
                props.organizations.find(
                  (item) =>
                    item.id ===
                    (values.organization ||
                      (organization !== 1 ? organization : undefined)),
                )?.name
              }
              placeholder="Şirket Seçiniz"
              className={globalStyles.width200px}
              disabled={organization !== 1}
              options={props.organizations.map((item) => ({
                label: item.name,
                value: item.id,
              }))}
              onChange={(value) => setFieldValue('organization', value)}
            />
            {touched.organization && errors.organization && (
              <ErrorMessage>{errors.organization}</ErrorMessage>
            )}
          </Flex>
        </Flex>
        <Flex gap="small" vertical>
          <SLabelTitle>Hasar Bilgileri</SLabelTitle>
          <Flex justify="space-between" gap="small">
            <Flex vertical gap="small" className={globalStyles.widthOneQuarter}>
              <SDropdown
                placeholder="Hasar Tipi"
                options={props.damageTypes.map((dt) => ({
                  value: dt.key,
                  label: dt.value,
                }))}
                value={values.damage_type}
                onChange={(value) => setFieldValue('damage_type', value)}
              />
              {touched.damage_type && errors.damage_type && (
                <ErrorMessage>{errors.damage_type}</ErrorMessage>
              )}
            </Flex>
            <Flex vertical gap="small" className={globalStyles.widthOneQuarter}>
              <SDropdown
                placeholder="Ülke"
                disabled={
                  values.damage_type === 'transportation' ||
                  values.damage_type === 'domestic'
                }
                options={props.countries.map((c) => ({
                  value: c.key,
                  label: c.value,
                }))}
                value={values.country}
                onChange={(value) => setFieldValue('country', value)}
              />
              {touched.country && errors.country && (
                <ErrorMessage>{errors.country}</ErrorMessage>
              )}
            </Flex>
            <Flex vertical gap="small" className={globalStyles.widthOneQuarter}>
              <SDropdown
                disabled={
                  values.damage_type === 'transportation' ||
                  values.damage_type === 'abroad'
                }
                placeholder="Şehir"
                options={props.cities.map((c) => ({
                  value: c.key,
                  label: c.value,
                }))}
                value={values.city}
                onChange={(value) => setFieldValue('city', value)}
              />
              {touched.city && errors.city && (
                <ErrorMessage>{errors.city}</ErrorMessage>
              )}
            </Flex>
            <Flex vertical gap="small" className={globalStyles.widthOneQuarter}>
              <SInput
                disabled={values.damage_type === 'transportation'}
                placeholder="Lokasyon Kodu"
                {...getFieldProps('location_code')}
              />
              {touched.location_code && errors.location_code && (
                <ErrorMessage>{errors.location_code}</ErrorMessage>
              )}
            </Flex>
          </Flex>
          <Flex justify="space-between" gap="small">
            <Flex vertical gap="small" className={globalStyles.widthOneQuarter}>
              <SDropdown
                placeholder="Hasar Branşı"
                options={props.branches.map((b) => ({
                  value: b.key,
                  label: b.value,
                }))}
                value={values.branch}
                onChange={(value) => setFieldValue('branch', value)}
              />
              {touched.branch && errors.branch && (
                <ErrorMessage>{errors.branch}</ErrorMessage>
              )}
            </Flex>
            <Flex vertical gap="small" className={globalStyles.widthOneQuarter}>
              <SDatePicker
                placeholder="Hasar Tarihi"
                value={
                  values.damage_date
                    ? dayjs(values.damage_date, DATE_FORMAT)
                    : null
                }
                onChange={(date) => {
                  if (date) {
                    setFieldValue('damage_date', date.format(DATE_FORMAT));
                  }
                }}
              />
              {touched.damage_date && errors.damage_date && (
                <ErrorMessage>{errors.damage_date}</ErrorMessage>
              )}
            </Flex>
            <Flex vertical gap="small" className={globalStyles.widthOneQuarter}>
              <SInput
                type="number"
                placeholder="Muallak Bedel"
                {...getFieldProps('pending_loss')}
              />
              {touched.pending_loss && errors.pending_loss && (
                <ErrorMessage>{errors.pending_loss}</ErrorMessage>
              )}
            </Flex>
            <Flex vertical gap="small" className={globalStyles.widthOneQuarter}>
              <SDropdown
                placeholder="Değer Birimi"
                options={props.currencies.map((c) => ({
                  value: c.key,
                  label: c.value,
                }))}
                value={values.currency}
                onChange={(value) => setFieldValue('currency', value)}
              />
              {touched.currency && errors.currency && (
                <ErrorMessage>{errors.currency}</ErrorMessage>
              )}
            </Flex>
          </Flex>
        </Flex>
        <Flex vertical gap="small">
          <Flex vertical gap="small">
            <SLabelTitle>Dosya Bilgileri</SLabelTitle>
            <Typography className={styles.description}>
              * Bu alandaki bilgiler Siba Sigorta tarafından doldurulacaktır.
            </Typography>
          </Flex>
          <Flex justify="space-between">
            <Flex vertical gap="small" className={globalStyles.widthOneThird}>
              <SInput
                placeholder="Dosya No"
                disabled={props.type === modalTypes.CREATE}
                {...getFieldProps('policy_number')}
              />
              {touched.policy_number && errors.policy_number && (
                <ErrorMessage>{errors.policy_number}</ErrorMessage>
              )}
            </Flex>
            <Flex vertical gap="small" className={globalStyles.widthOneThird}>
              <SInput
                placeholder="Eksper"
                disabled={props.type === modalTypes.CREATE}
                {...getFieldProps('expert')}
              />
              {touched.expert && errors.expert && (
                <ErrorMessage>{errors.expert}</ErrorMessage>
              )}
            </Flex>
            <Flex vertical gap="small" className={globalStyles.widthOneThird}>
              <SInput
                placeholder="Raportör"
                disabled={props.type === modalTypes.CREATE}
                {...getFieldProps('reporter')}
              />
              {touched.reporter && errors.reporter && (
                <ErrorMessage>{errors.reporter}</ErrorMessage>
              )}
            </Flex>
          </Flex>
          <UploadButton
            fileName={[...values.upload_files].map((item) => item.name)}
            uploadRef={uploadInput}
            onChange={props.handleUploadFile}
          />
          {props.type === modalTypes.EDIT && organization === 1 && (
            <Flex justify="space-between">
              <SLabelTitle>Durum Bilgisi</SLabelTitle>
              <StatusDropdown
                status={values.recent_status}
                statusChoices={props.statusChoices}
                onChange={(value) => setFieldValue('recent_status', value)}
              />
            </Flex>
          )}
        </Flex>
        <Flex vertical gap="small">
          <SLabelTitle>Açıklama</SLabelTitle>
          <Flex vertical>
            <STextArea
              placeholder="Açıklama"
              {...getFieldProps('description')}
              rows={3}
            />
            {touched.description && errors.description && (
              <ErrorMessage>{errors.description}</ErrorMessage>
            )}
          </Flex>
        </Flex>
        <Typography className={styles.time}>
          {dayjs().format('DD/MM/YYYY - HH:mm')}
        </Typography>
        {props.type === modalTypes.EDIT && (
          <SMailSelector
            value={values.email_users}
            placeholder="Mail Listesi"
            options={props.withViewPerm?.map((wvp) => ({
              value: wvp.value,
              label: wvp.label,
            }))}
            onChange={(value) => setFieldValue('email_users', value)}
          />
        )}
      </Flex>
    </ModalDialog>
  );
};

export default DamageNotificationModal;
